import React, { useState, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { getSettings, updateSettings } from '../services/api';

const Section = ({ icon, title, children }) => (
  <div className="card mb-4">
    <div style={{ padding: '18px 22px 0', display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
    </div>
    <div style={{ borderTop: '1px solid var(--border)', padding: '20px 22px' }}>
      {children}
    </div>
  </div>
);

const FieldRow = ({ label, hint, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 18 }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{hint}</div>}
    </div>
    <div style={{ width: 280, flexShrink: 0 }}>{children}</div>
  </div>
);

function Toggle({ checked, onChange }) {
  return (
    <div onClick={onChange} style={{
      width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
      background: checked ? 'var(--primary)' : '#9ca3af',
      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
    }}>
      <div style={{
        position: 'absolute', top: 3, left: checked ? 23 : 3,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
      }} />
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    announcement_enabled: 'false',
    announcement_text:    '',
    announcement_type:    'info',
    company_name:         'ITSM Tool',
    sla_critical:         '4',
    sla_high:             '8',
    sla_medium:           '24',
    sla_low:              '72',
    email_enabled:        'false',
    smtp_host:            '',
    smtp_port:            '587',
    smtp_user:            '',
    smtp_pass:            '',
    smtp_from:            'ITSM Tool <noreply@itsm.local>',
  });
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState({ type: '', text: '' });
  const [dirty,    setDirty]    = useState(false);

  useEffect(() => {
    getSettings()
      .then(res => setSettings(s => ({ ...s, ...res.data })))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const set = (key, value) => {
    setSettings(s => ({ ...s, [key]: String(value) }));
    setDirty(true);
    setMsg({ type: '', text: '' });
  };

  const handleSave = async () => {
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      await updateSettings(settings);
      setMsg({ type: 'success', text: 'Settings saved successfully!' });
      setDirty(false);
      // Refresh page storage so announcement banner updates immediately
      window.__itsmSettings = settings;
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save settings.' });
    } finally { setSaving(false); }
  };

  const announcementEnabled = settings.announcement_enabled === 'true';

  const ANNOUNCEMENT_TYPES = [
    { value: 'info',    label: 'ℹ️ Info',    color: '#3b82f6' },
    { value: 'success', label: '✅ Success', color: '#10b981' },
    { value: 'warning', label: '⚠️ Warning', color: '#f59e0b' },
    { value: 'error',   label: '🔴 Alert',   color: '#ef4444' },
  ];

  return (
    <AppLayout pageTitle="System Settings">
      {loading ? (
        <div className="flex-center" style={{ padding: 80 }}>
          <div className="spinner" style={{ width: 36, height: 36 }} />
        </div>
      ) : (
        <div style={{ maxWidth: 780, margin: '0 auto' }}>

          {/* ── Announcement Banner ── */}
          <Section icon="📢" title="Announcement Banner">
            <FieldRow
              label="Enable Banner"
              hint="Show a system-wide announcement to all users at the top of every page."
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Toggle checked={announcementEnabled} onChange={() => set('announcement_enabled', !announcementEnabled)} />
                <span style={{ fontSize: 13, color: announcementEnabled ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 600 }}>
                  {announcementEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </FieldRow>

            <FieldRow label="Banner Type" hint="Color and icon style of the announcement.">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ANNOUNCEMENT_TYPES.map(t => (
                  <button key={t.value} onClick={() => set('announcement_type', t.value)} style={{
                    padding: '5px 12px', borderRadius: 8, border: '1.5px solid',
                    borderColor: settings.announcement_type === t.value ? t.color : 'var(--border)',
                    background:  settings.announcement_type === t.value ? `${t.color}18` : 'transparent',
                    color:       settings.announcement_type === t.value ? t.color : 'var(--text-muted)',
                    fontSize: 12.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                  }}>{t.label}</button>
                ))}
              </div>
            </FieldRow>

            <FieldRow label="Message" hint="Text to display in the announcement banner.">
              <textarea
                className="form-control"
                value={settings.announcement_text}
                onChange={e => set('announcement_text', e.target.value)}
                placeholder="e.g. Scheduled maintenance on Saturday 10 PM – 12 AM."
                rows={3}
                style={{ resize: 'vertical', fontSize: 13 }}
              />
            </FieldRow>

            {/* Preview */}
            {settings.announcement_text && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: '0.5px' }}>
                  PREVIEW
                </div>
                <AnnouncementPreview
                  type={settings.announcement_type}
                  text={settings.announcement_text}
                  enabled={announcementEnabled}
                />
              </div>
            )}
          </Section>

          {/* ── General ── */}
          <Section icon="🏢" title="General">
            <FieldRow label="Company / System Name" hint="Displayed in the sidebar and emails.">
              <input
                className="form-control"
                value={settings.company_name}
                onChange={e => set('company_name', e.target.value)}
                placeholder="e.g. Acme Corp ITSM"
              />
            </FieldRow>
          </Section>

          {/* ── SLA Configuration ── */}
          <Section icon="⏱️" title="SLA Response Hours">
            <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-muted)' }}>
              Maximum hours allowed to resolve a ticket by priority level. New tickets will use these values.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[
                { key: 'sla_critical', label: '🔴 Critical', color: '#ef4444' },
                { key: 'sla_high',     label: '🟠 High',     color: '#f97316' },
                { key: 'sla_medium',   label: '🟡 Medium',   color: '#f59e0b' },
                { key: 'sla_low',      label: '🟢 Low',      color: '#22c55e' },
              ].map(s => (
                <div key={s.key}>
                  <label style={{
                    display: 'block', fontSize: 12, fontWeight: 700,
                    color: s.color, marginBottom: 6,
                  }}>{s.label}</label>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      type="number" min="1" max="720"
                      className="form-control"
                      value={settings[s.key]}
                      onChange={e => set(s.key, e.target.value)}
                      style={{ paddingRight: 48 }}
                    />
                    <span style={{
                      position: 'absolute', right: 12,
                      fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)',
                      pointerEvents: 'none',
                    }}>hrs</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                    = {Math.round(Number(settings[s.key]) / 24 * 10) / 10} day{Number(settings[s.key]) >= 24 ? 's' : ''}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* ── Email / SMTP ── */}
          <Section icon="✉️" title="Email Notifications (SMTP)">
            <FieldRow label="Enable Email" hint="Send emails for ticket assignments, SLA breaches, and leave decisions.">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Toggle
                  checked={settings.email_enabled === 'true'}
                  onChange={() => set('email_enabled', settings.email_enabled !== 'true')}
                />
                <span style={{ fontSize: 13, color: settings.email_enabled === 'true' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: 600 }}>
                  {settings.email_enabled === 'true' ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </FieldRow>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={smtpLabel}>SMTP Host</label>
                <input className="form-control" value={settings.smtp_host} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" />
              </div>
              <div>
                <label style={smtpLabel}>Port</label>
                <input className="form-control" type="number" value={settings.smtp_port} onChange={e => set('smtp_port', e.target.value)} placeholder="587" />
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16, lineHeight: 1.6 }}>
              <strong>Outlook / Microsoft 365:</strong> host <code>smtp.office365.com</code>, port <code>587</code> ·&nbsp;
              <strong>Gmail:</strong> host <code>smtp.gmail.com</code>, port <code>587</code>.
              SMTP AUTH must be enabled on the mailbox.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={smtpLabel}>SMTP Username</label>
                <input className="form-control" value={settings.smtp_user} onChange={e => set('smtp_user', e.target.value)} placeholder="user@company.com" />
              </div>
              <div>
                <label style={smtpLabel}>SMTP Password</label>
                <input className="form-control" type="password" value={settings.smtp_pass} onChange={e => set('smtp_pass', e.target.value)} placeholder="••••••••" />
              </div>
            </div>
            <FieldRow label="From Address" hint="Only the display name is used — mail is always sent from the SMTP Username above (Outlook/Gmail reject any other sender).">
              <input className="form-control" value={settings.smtp_from} onChange={e => set('smtp_from', e.target.value)} placeholder="ITSM Tool <noreply@company.com>" />
            </FieldRow>
          </Section>

          {/* ── Save bar ── */}
          {msg.text && (
            <div style={{
              padding: '11px 16px', borderRadius: 10, fontSize: 13, marginBottom: 14,
              background: msg.type === 'success' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
              color:      msg.type === 'success' ? '#059669'               : '#dc2626',
              border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            }}>
              {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, paddingBottom: 32 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving || !dirty}
              style={{ minWidth: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              {saving
                ? <><span style={spinSt}/> Saving…</>
                : '💾 Save Settings'
              }
            </button>
            {dirty && (
              <button className="btn btn-secondary" onClick={() => {
                getSettings().then(res => { setSettings(s => ({ ...s, ...res.data })); setDirty(false); setMsg({ type: '', text: '' }); });
              }}>
                ✕ Discard Changes
              </button>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}

const BANNER_STYLES = {
  info:    { bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.3)',  color: '#1d4ed8', icon: 'ℹ️'  },
  success: { bg: 'rgba(16,185,129,0.10)',  border: 'rgba(16,185,129,0.3)',  color: '#065f46', icon: '✅'  },
  warning: { bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.3)',  color: '#92400e', icon: '⚠️' },
  error:   { bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.3)',   color: '#991b1b', icon: '🔴' },
};

function AnnouncementPreview({ type, text, enabled }) {
  const s = BANNER_STYLES[type] || BANNER_STYLES.info;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 8, padding: '9px 14px',
      opacity: enabled ? 1 : 0.5,
    }}>
      <span style={{ fontSize: 15, flexShrink: 0 }}>{s.icon}</span>
      <span style={{ fontSize: 13, color: s.color, fontWeight: 500 }}>{text}</span>
      {!enabled && (
        <span style={{
          marginLeft: 'auto', fontSize: 10.5, fontWeight: 700,
          background: 'rgba(0,0,0,0.08)', color: 'var(--text-muted)',
          padding: '2px 7px', borderRadius: 4,
        }}>DISABLED</span>
      )}
    </div>
  );
}

const smtpLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };

const spinSt = {
  display: 'inline-block', width: 14, height: 14,
  border: '2px solid rgba(255,255,255,0.35)',
  borderTopColor: '#fff', borderRadius: '50%',
  animation: 'spin 0.7s linear infinite',
};
